/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment } from 'react'
import { injectIntl } from 'react-intl'
import { Link } from 'react-router-dom'

import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiPopover,
  EuiPopoverTitle,
  EuiText,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { InstanceConfiguration } from '@modules/cloud-api/v1/types'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiTable } from '@modules/cui/Table'
import { useToggle } from '@modules/utils/hooks/useToggle'

import { CuiSliderLogo } from '@/components/SliderLogo'
import { getSliderPrettyName } from '@/lib/sliders/messages'
import {
  getPrimarySliderTypeForInstanceConfiguration,
  getSliderTypesForInstanceConfiguration,
} from '@/lib/sliders/transform'

import { isDeprecatedConfig } from '../../../../lib/instanceConfigurations/instanceConfiguration'
import { topologyViewInstanceConfigurationUrl } from '../../../../lib/urlBuilder'
import { getDeprecationWarningBody, getDeprecationWarningTitle } from '../DeprecationWarning'

import type { SerializedStyles } from '@emotion/react'
import type { IntlShape } from 'react-intl'

export type Props = {
  intl: IntlShape
  instanceConfigurations?: InstanceConfiguration[]
  regionId: string
  initialLoading: boolean
}

const InstanceConfigurationsTable = ({
  intl,
  instanceConfigurations,
  regionId,
  initialLoading,
}: Props) => {
  const [isPopoverOpen, togglePopover, setPopoverOpen] = useToggle()
  const theme = useEuiTheme()

  const makeRowStyle = (instanceConfiguration: InstanceConfiguration): SerializedStyles =>
    css(
      isDeprecatedConfig(instanceConfiguration) && {
        '&, &:hover': {
          backgroundColor: theme.euiTheme.colors.backgroundLightWarning,
        },
      },
    )

  const anyDeprecated = instanceConfigurations && instanceConfigurations.some(isDeprecatedConfig)

  const columns: Array<CuiTableColumn<InstanceConfiguration>> = [
    {
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='topologyinstanceConfigurations.table.nameHeader'
          defaultMessage='Name'
        />
      ),
      render: (instanceConfiguration) => (
        <Fragment>
          <Link to={topologyViewInstanceConfigurationUrl(regionId, instanceConfiguration.id!)}>
            {instanceConfiguration.name}
          </Link>
          <div>{instanceConfiguration.description}</div>
        </Fragment>
      ),
      sortKey: `name`,
    },
    {
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='topologyinstanceConfigurations.table.nodeTypesHeader'
          defaultMessage='Instance types'
        />
      ),
      render: (instanceConfiguration) => {
        const sliderTypes = getSliderTypesForInstanceConfiguration(instanceConfiguration)
          .map((sliderType) => intl.formatMessage(getSliderPrettyName(sliderType)))
          .join(`, `)

        return (
          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
            <EuiFlexItem grow={false}>
              <CuiSliderLogo
                {...getPrimarySliderTypeForInstanceConfiguration(instanceConfiguration)}
              />
            </EuiFlexItem>

            <EuiFlexItem>{sliderTypes}</EuiFlexItem>
          </EuiFlexGroup>
        )
      },
    },
    {
      width: `8rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='topologyinstanceConfigurations.table.createdByHeader'
          defaultMessage='Created by'
        />
      ),
      render: (instanceConfiguration) => (
        <EuiBadge color='hollow'>
          {instanceConfiguration.system_owned ? (
            <FormattedMessage
              id='topologyinstanceConfigurations.table.system'
              defaultMessage='System'
            />
          ) : (
            <FormattedMessage
              id='topologyinstanceConfigurations.table.user'
              defaultMessage='User'
            />
          )}
        </EuiBadge>
      ),
      sortKey: `system_owned`,
    },
  ]

  if (anyDeprecated) {
    columns.push({
      width: `10rem`,
      verticalAlign: `top`,
      label: (
        <FormattedMessage
          id='topologyinstanceConfigurations.table.additionalInformationHeader'
          defaultMessage='Additional information'
        />
      ),
      render: (instanceConfiguration) => {
        const isDeprecated = isDeprecatedConfig(instanceConfiguration)

        if (!isDeprecated) {
          return null
        }

        const popoverId = `${instanceConfiguration.id}-deprecatedPopover`

        return (
          <EuiPopover
            id={popoverId}
            button={
              <div style={{ maxWidth: `8rem` }}>
                <EuiLink onClick={togglePopover}>
                  <FormattedMessage
                    id='topologyinstanceConfigurations.table.deprecatedTrigger'
                    defaultMessage='Deprecated configuration'
                  />
                  {` `}
                  <EuiIcon type='info' />
                </EuiLink>
              </div>
            }
            anchorPosition='downRight'
            isOpen={isPopoverOpen}
            closePopover={() => setPopoverOpen(false)}
          >
            <EuiPopoverTitle>
              <FormattedMessage {...getDeprecationWarningTitle({ instanceConfiguration })} />
            </EuiPopoverTitle>
            <div style={{ maxWidth: `22rem` }}>
              <EuiText>
                <FormattedMessage {...getDeprecationWarningBody({ instanceConfiguration })} />
              </EuiText>
            </div>
          </EuiPopover>
        )
      },
    })
  }

  return (
    <CuiTable
      rows={instanceConfigurations}
      columns={columns}
      getRowId={(instanceConfiguration) => instanceConfiguration.id!}
      rowStyle={makeRowStyle}
      initialLoading={initialLoading}
    />
  )
}

export default injectIntl(InstanceConfigurationsTable)
